import { getSelectedBrandPath, getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateByPath } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const currentPageData = getCurrentPage(state);
    const productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, productPath);
    const productState = getProductStateByPath(state, productPath);
    const brandPath = getSelectedBrandPath(state) || location.pathname;
    const brandState = getBrandStateByPath(state, brandPath);

    const catalogOgImage = catalogPageState.value?.openGraphImage || catalogPageState.value?.primaryImagePath;
    const productdetailOgImage =
        productState.value?.mediumImagePath || productState.value?.largeImagePath || productState.value?.smallImagePath;
    const brandOgImage = brandState.value?.logoSmallImagePath || brandState.value?.logoLargeImagePath;
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);

    return {
        breadcrumbLinks: state.components.breadcrumbs.links,
        currentPageData,
        location,
        website: state.context.website,
        websiteName: state.context.website.name,
        product: computedVariantProduct.value,
        catalogOgImage,
        productdetailOgImage,
        brandOgImage,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
        orderList: state.data?.orders?.byId,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

const CENCustomSchema: React.FC<Props> = (props: Props) => {
    const {
        breadcrumbLinks,
        location,
        website,
        catalogOgImage,
        productdetailOgImage,
        brandOgImage,
        currentPageData,
        product,
        websiteName,
        totalCountDisplay = 0,
        isAuthenticated,
        orderList
    } = props;

    if (typeof window === "undefined" && typeof document === "undefined") {
        return null; // Prevent execution on the server side
    }
    const { fields } = currentPageData;
    const windowsOrigin = window?.location?.origin;
    const href = window?.location?.href;
    const isPDP = currentPageData?.type === "ProductDetailsPage";
    const isProductListPage = currentPageData?.type === "ProductListPage";
    const isBrandPage = currentPageData?.type === "BrandPage" || currentPageData?.type === "BrandDetailsPage";

    if (isPDP && (!breadcrumbLinks || breadcrumbLinks.length === 0)) {
        return null;
    }

    const setMetaTag = (name: string, content: string) => {
        let tag = document.querySelector(
            name.includes("twitter") ? `meta[name='${name}']` : `meta[property='${name}']`,
        );
        if (!tag) {
            tag = document.createElement("meta");
            name.includes("twitter") ? tag.setAttribute("name", name) : tag.setAttribute("property", name);
            document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
    };

    const getMetaDescription = (): string => {
        const metaDescriptionElement = document.querySelector('meta[name="description"]');
        return metaDescriptionElement?.getAttribute("content") || "";
    };

    const normalizeTitle = (value?: string) =>
        value
            ? value
                .normalize()
                .replace(/\u00A0/g, " ")
                .replace(/\s+/g, " ")
                .trim()
            : "";

    useEffect(() => {
        const websiteLogo =
            websiteName === "Nisco"
                ? `${windowsOrigin}/userfiles/images/nisco-logo.png`
                : `${windowsOrigin}/userfiles/centro_logo.png`;
        // Setting Twitter meta tags dynamically
        const title = document.title;
        const description = getMetaDescription();
        // let description = document.querySelector('meta[name="description"]');
        const ogImage = document.querySelector('meta[property="og:image"]');
        const imgUrl =
            fields["openGraphImage"] || isPDP
                ? productdetailOgImage || websiteLogo
                : isBrandPage && brandOgImage
                    ? `${windowsOrigin}${brandOgImage}`
                    : ogImage?.getAttribute("content") || catalogOgImage || productdetailOgImage || websiteLogo;
        const productDetailTitle = isPDP && product ? `Buy ${product.productTitle} Online | ${websiteName}` : "";
        let finalMetaTitle = title;
        if (normalizeTitle(title) === normalizeTitle(product?.productTitle)) {
            finalMetaTitle = "";
        }

        // Update document.title if fields?.overrideSeoTitle is present
        const metaTitle = finalMetaTitle || productDetailTitle || fields?.overrideSeoTitle;
        if (fields?.overrideSeoTitle) {
            document.title = metaTitle;
        }

        setMetaTag("twitter:card", website.name);
        !isPDP && setMetaTag("twitter:title", metaTitle);
        !isPDP && setMetaTag("twitter:description", description || "");
        setMetaTag("twitter:image", imgUrl);
        setMetaTag("twitter:url", href);
        setTimeout(() => {
            setMetaTag("og:title", metaTitle);
            setMetaTag("og:description", description || "");
            !isProductListPage && setMetaTag("og:image", imgUrl);
            setMetaTag("og:url", href);
        }, 300);

        // Clean up function to remove meta tags when component is unmounted
        return () => {
            document
                .querySelectorAll("meta[name^='twitter:'], meta[property^='og:']")
                .forEach(tag => document.head.removeChild(tag));
        };
    }, [location, catalogOgImage, productdetailOgImage, brandOgImage, breadcrumbLinks]);

    useEffect(() => {
        const existingEmptyScript = document.querySelector('script[type="application/ld+json"]');
        if (existingEmptyScript && existingEmptyScript.innerHTML.trim() === "") {
            existingEmptyScript.remove();
        }

        const languageRegion = "en-us";
        const linkTag = document.createElement("link");
        linkTag.rel = "alternate";
        linkTag.hreflang = languageRegion;
        linkTag.href = href;

        const existingLink = document.head.querySelector(`link[rel="alternate"][hreflang="${languageRegion}"]`);
        if (existingLink) {
            document.head.removeChild(existingLink);
        }

        document.head.appendChild(linkTag);

        return () => {
            if (document.head.contains(linkTag)) {
                document.head.removeChild(linkTag);
            }
        };
    }, [location, href]);

    useEffect(() => {
        // Read gclid from URL
        const params = new URLSearchParams(window.location.search);
        const gclid = params.get("gclid");

        // Save into sessionStorage
        if (gclid) sessionStorage.setItem("gclid", gclid);
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        setTimeout(() => {
            const orderCount = Object.keys(orderList || {}).length;
            let customerType = "prospect";
            if (orderCount === 1) customerType = "first_time_buyer";
            else if (orderCount > 1) customerType = "repeat_buyer";
            
            const activeCart = totalCountDisplay > 0 ? "true" : "false";
            const isAuth = isAuthenticated ? "true" : "false";
    
            const optimizelyAttributes = {
                logged_in: isAuth,
                customer_type: customerType,
                has_active_cart: activeCart,
            };
            const optimizely = (window as any).optimizely;
            // Set Optimizely attributes
            optimizely.push({
                type: "user",
                attributes: optimizelyAttributes,
            });
        }, 500);
        
    }, [totalCountDisplay, isAuthenticated, orderList]);

    return null;
};

const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "Custom Schema",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CENCustomSchema),
    definition,
};

export default widgetModule;